import React from 'react';
import { cn } from '~/lib/utils';
import styles from './SkeletonCard.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * One grey bar.
 *
 * `h` is a two-member union rather than a number because the design draws exactly two bar heights
 * and each carries its own radius — 9px bars are 3px round, 16px bars are 4px. Letting a caller
 * pass 12 would put a height on screen with no radius to go with it.
 */
export interface SkeletonBar {
  /** Bar width in px. The file draws 120, 190, 240, 280 and 300. */
  w: number;
  /** 9 = the eyebrow rule · 16 = a line of body type. */
  h: 9 | 16;
  /** `weak` = light-10 · `strong` = light-15. They alternate; they do not ramp. */
  tone: 'weak' | 'strong';
}

export interface SkeletonCardProps {
  /**
   * Defaults to the canonical three — 120x9 weak, 280x16 strong, 190x16 weak — which is what
   * *Writing artifact*, *Canvas* and *Founder · cold start* all draw. Pass your own for the two
   * variants that differ: Agent draft widens bars 2 and 3 to 300 and 240, and Queue drains is a
   * single 240x16 strong bar.
   */
  bars?: SkeletonBar[];
  theme?: ChatTheme;
  className?: string;
}

/** 120x9 weak, 280x16 strong, 190x16 weak — eyebrow, headline, subline. */
const DEFAULT_BARS: SkeletonBar[] = [
  { w: 120, h: 9, tone: 'weak' },
  { w: 280, h: 16, tone: 'strong' },
  { w: 190, h: 16, tone: 'weak' },
];

/** The 9px bar rounds at 3, the 16px bar at 4. Literal px in the file, not radius tokens. */
const BAR_RADIUS: Record<SkeletonBar['h'], string> = { 9: '3px', 16: '4px' };

const TONE_CLASS: Record<SkeletonBar['tone'], string> = {
  weak: styles.weak,
  strong: styles.strong,
};

/**
 * The preview body of an activity card: a low-fidelity sketch of the artifact being written, so
 * its shape is legible before its content exists.
 *
 * *Writing artifact*'s caption is the whole brief — *a skeleton of the artifact, not a progress
 * bar*. Nothing here reports completion: the bars are a fixed 120/280/190, they do not fill, and
 * the count that would make this a meter lives in the activity row above it. It is the sibling of `ActivityList`
 * and differs from it in exactly three ways — 14px of padding all round instead of a 4px block
 * inset, a 9px gap instead of none, and no `overflow: clip`.
 *
 * The card is decorative by construction. Every artboard puts it directly under an activity row
 * that already says "Writing Retention · artifact 02" in words, so announcing three unlabelled
 * rectangles underneath would add noise and no news — hence `aria-hidden`. The row above owns the
 * `aria-live` region for the state change.
 *
 * It also does not move. The prop surface is `{ bars?, theme? }` and nothing else, per §1.5 — the
 * running/settled distinction the artboards draw is carried by the activity row's own glyph and
 * label, so a busy skeleton and a finished one are the same box. See the tail of the stylesheet
 * for why the breathing loop that used to sit behind a `live` prop is gone.
 */
export function SkeletonCard({ bars = DEFAULT_BARS, theme: themeProp, className }: SkeletonCardProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.root, className)} aria-hidden="true">
      {bars.map((bar, i) => (
        <div
          // Bars are a positional sketch, not data — two bars in one card can legitimately be the
          // same width and tone, so the index is the only stable identity a bar has. The list is
          // static for the life of a card, so there is nothing for a better key to buy.
          key={i}
          className={cn(styles.bar, TONE_CLASS[bar.tone])}
          // The three runtime values, and the only reason this component inline-styles anything.
          style={
            {
              '--cv15-bar-w': `${bar.w}px`,
              '--cv15-bar-h': `${bar.h}px`,
              '--cv15-bar-radius': BAR_RADIUS[bar.h],
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
